import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import API, { API_BASE } from '../lib/api';
import { toast } from 'sonner';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '633411294867-3s8ce654gdqia8gk6ebv37da088humm9.apps.googleusercontent.com';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login, loginWithGoogle } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Handle redirect auth tokens from Google redirect flow
  useEffect(() => {
    const authToken = searchParams.get('auth_token');
    const refreshToken = searchParams.get('refresh_token');
    const authError = searchParams.get('error');
    if (authError) {
      setError(decodeURIComponent(authError));
      // Clean URL
      window.history.replaceState({}, '', '/login');
      return;
    }
    if (authToken && refreshToken) {
      // Store tokens and fetch user data
      localStorage.setItem('access_token', authToken);
      localStorage.setItem('refresh_token', refreshToken);
      API.get('/auth/me').then(res => {
        loginWithGoogle(res.data);
        navigate('/', { replace: true });
      }).catch(() => {
        setError('Authentication failed. Please try again.');
      });
      // Clean URL
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams, loginWithGoogle, navigate]);

  // Redirect if already logged in
  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  // Handle popup Google response (desktop)
  const handleGoogleResponse = useCallback(async (response) => {
    setLoading(true);
    setError('');
    try {
      const res = await API.post('/auth/google-login', { credential: response.credential });
      loginWithGoogle(res.data);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
      setLoading(false);
    }
  }, [navigate, loginWithGoogle]);

  // Load Google Identity Services
  useEffect(() => {
    if (user) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      // Google's authorized redirect URIs must include
      // `${API_BASE}/api/auth/google-redirect` (the BACKEND origin).
      window.google?.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
        auto_select: false,
        ...(isMobile ? {
          ux_mode: 'redirect',
          login_uri: `${API_BASE}/api/auth/google-redirect`,
        } : {}),
      });
      const el = document.getElementById('google-signin-btn');
      if (el) {
        window.google?.accounts.id.renderButton(el, {
          theme: 'filled_black', size: 'large', width: 320, text: 'signin_with',
        });
      }
    };
    document.head.appendChild(script);
    return () => { try { document.head.removeChild(script); } catch {} };
  }, [user, handleGoogleResponse]);

  const handlePasswordLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
      setLoading(false);
    }
  };

  return (
    <div className="h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-8 text-center">
        <div>
          <img src="/filmbaker-logo.png" alt="Filmbaker" className="h-14 mx-auto mb-2 drop-shadow-[0_0_12px_rgba(255,255,255,0.15)]" data-testid="login-logo" />
          <p className="text-zinc-400 text-base tracking-[0.5em] uppercase font-medium mt-1" style={{letterSpacing: '0.5em'}}>ENGINE</p>
        </div>
        {error && <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-lg p-3" data-testid="login-error">{error}</p>}
        <div className="space-y-4">
          <div id="google-signin-btn" className="flex justify-center" data-testid="google-signin-btn" />
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-zinc-800" />
            <button onClick={() => setShowPassword(!showPassword)} className="text-xs text-zinc-600 hover:text-zinc-400 transition" data-testid="toggle-password-login">
              {showPassword ? 'Hide' : 'Login with Email'}
            </button>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>
          {showPassword && (
            <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
              <Input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className="bg-zinc-900 border-zinc-800 text-white" data-testid="login-email" />
              <Input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handlePasswordLogin()} className="bg-zinc-900 border-zinc-800 text-white" data-testid="login-password" />
              <Button onClick={handlePasswordLogin} disabled={loading} className="w-full bg-white text-black hover:bg-zinc-200 font-semibold" data-testid="login-submit">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
